if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          console.log(html, 'html code')
          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
  if (!variantFeaturedMediaId) return;

  // robust sectionId detection (Dawn stores on section)
  const sectionEl = this.closest('[data-section-id]');
  const sectionId = sectionEl ? sectionEl.dataset.sectionId : this.dataset.section;

  // Helper: try unslicking the current slick instance for this section
  const safeUnslick = () => {
    try {
      if (window.jQuery) {
        const $g = window.jQuery(`#Slider-Gallery-${sectionId}`);
        if ($g.length && $g.hasClass('slick-initialized')) {
          $g.slick('unslick');
        }
      }
    } catch (e) {
      console.warn('safeUnslick error', e);
      // best effort: remove slick data/class
      try {
        const el = document.querySelector(`#Slider-Gallery-${sectionId}`);
        if (el) {
          el.classList.remove('slick-initialized');
          if (el._slick) delete el._slick;
        }
      } catch (e2) {}
    }
  };

  // locate source and destination lists
  const mediaGallerySource = this.querySelector('media-gallery ul');
  const mediaGalleryDestination = html.querySelector('media-gallery ul');

  if (!mediaGallerySource || !mediaGalleryDestination) {
    // still update active media and modal if lists aren't present
    this.querySelector('media-gallery')?.setActiveMedia?.(
      `${this.dataset.section || sectionId}-${variantFeaturedMediaId}`,
      false
    );

    const modalContent = this.productModal?.querySelector('.product-media-modal__content');
    const newModalContent = html.querySelector('product-modal .product-media-modal__content');
    if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
    // attempt reinit
    setTimeout(() => window.customSlider?.(sectionId), 40);
    return;
  }

  // Unslick before DOM changes to avoid mismatch
  safeUnslick();

  const refreshSourceData = () => {
    if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
    const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
    const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
    const sourceMap = new Map(
      mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
    );
    return [mediaGallerySourceItems, sourceSet, sourceMap];
  };

  let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
  const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
  const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
  let shouldRefresh = false;

  // Add missing items (insert in destination order)
  mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
    const did = destinationItem.dataset.mediaId;
    if (!sourceSet.has(did)) {
      const referenceNode = mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`);
      if (!referenceNode) mediaGallerySource.appendChild(destinationItem);
      else mediaGallerySource.insertBefore(destinationItem, referenceNode);
      shouldRefresh = true;
    }
  });

  // Remove items no longer present
  mediaGallerySourceItems.forEach((srcItem) => {
    if (!destinationSet.has(srcItem.dataset.mediaId)) {
      srcItem.remove();
      shouldRefresh = true;
    }
  });

  // Refresh if changed
  if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

  // Reorder existing items to match destination order
  mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
    const did = destinationItem.dataset.mediaId;
    const sourceData = sourceMap.get(did);
    if (!sourceData) return;
    const referenceNode = mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`);
    if (!referenceNode) {
      mediaGallerySource.appendChild(sourceData.item);
      [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
      return;
    }
    if (referenceNode !== sourceData.item) {
      if (referenceNode.parentElement === mediaGallerySource) {
        mediaGallerySource.insertBefore(sourceData.item, referenceNode);
      } else {
        // fallback: append
        mediaGallerySource.appendChild(sourceData.item);
      }
      [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
    }
  });

  // Compose id used by setActiveMedia
  const composedMediaId = `${this.dataset.section || sectionId}-${variantFeaturedMediaId}`;

  // Set featured media active WITHOUT prepending (avoid moving DOM)
  this.querySelector('media-gallery')?.setActiveMedia?.(composedMediaId, false);

  // ----------------- Sync thumbnails DOM to match destination order -----------------
  try {
    // Normalization helper to strip possible section prefix for robust matching
    const normalize = (id) => String(id || '').replace(new RegExp(`^${this.dataset.section || sectionId}-`), '');

    // Find live thumbnails container for this section
    let thumbsContainer =
      this.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
      document.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
      this.querySelector('[id^="Slider-Thumbnails"]') ||
      document.querySelector('[id^="Slider-Thumbnails"]');

    // destination thumbnail items from html fragment
    const destinationThumbItems =
      (html.querySelector(`#Slider-Thumbnails-${sectionId}`) &&
        Array.from(html.querySelector(`#Slider-Thumbnails-${sectionId}`).querySelectorAll('.thumbnail-list__item'))) ||
      (html.querySelector('[id^="Slider-Thumbnails"]') && Array.from(html.querySelector('[id^="Slider-Thumbnails"]').querySelectorAll('.thumbnail-list__item'))) ||
      [];

    if (thumbsContainer && destinationThumbItems.length) {
      const liveThumbs = Array.from(thumbsContainer.querySelectorAll('.thumbnail-list__item'));
      const liveMap = new Map(liveThumbs.map((t) => [normalize(t.dataset.target), t]));

      destinationThumbItems.forEach((destThumbItem, idx) => {
        const destTarget = normalize(destThumbItem.dataset.target || destThumbItem.dataset.mediaId);
        const liveNode = liveMap.get(destTarget);
        if (liveNode) {
          const ref = thumbsContainer.querySelector(`.thumbnail-list__item:nth-of-type(${idx + 1})`);
          if (!ref) thumbsContainer.appendChild(liveNode);
          else thumbsContainer.insertBefore(liveNode, ref);
        } else {
          const ref = thumbsContainer.querySelector(`.thumbnail-list__item:nth-of-type(${idx + 1})`);
          if (!ref) thumbsContainer.appendChild(destThumbItem);
          else thumbsContainer.insertBefore(destThumbItem, ref);
        }
      });

      // Remove any leftover live thumbs not in destination
      Array.from(thumbsContainer.querySelectorAll('.thumbnail-list__item')).forEach((t) => {
        const key = normalize(t.dataset.target);
        if (!destinationThumbItems.find((d) => normalize(d.dataset.target || d.dataset.mediaId) === key)) {
          t.remove();
        }
      });
    }
  } catch (e) {
    console.warn('thumbnail-sync failed', e);
  }
  // ----------------- end thumbnail sync ----------------------------------------------

  // update modal content if present
  const modalContent = this.productModal?.querySelector('.product-media-modal__content');
  const newModalContent = html.querySelector('product-modal .product-media-modal__content');
  if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

  // Small tick then re-init slider for this section (rebuilds slick with new DOM order)
  setTimeout(() => {
    try {
      if (typeof window.customSlider === 'function') {
        window.customSlider(sectionId);
      } else {
        window.customSlider?.();
      }
    } catch (e) {
      console.warn('reinit slider failed', e);
    }

    // After reinit, wait and then sync slick & thumbnails using original (non-cloned) slides
    setTimeout(() => {
      try {
        const normalize = (id) => String(id || '').replace(new RegExp(`^${this.dataset.section || sectionId}-`), '');
        let activeIndex = -1;

        if (window.jQuery && window.jQuery.fn.slick) {
          const $main = window.jQuery(`#Slider-Gallery-${sectionId}`);
          if ($main.length && $main.hasClass('slick-initialized')) {
            // select original slides only (exclude cloned)
            const $originalSlides = $main.find('.slick-slide').not('.slick-cloned');

            $originalSlides.each(function (i) {
              const mediaEl = window.jQuery(this).find('[data-media-id]')[0];
              if (mediaEl) {
                if (normalize(mediaEl.dataset.mediaId) === normalize(composedMediaId) || mediaEl.dataset.mediaId === composedMediaId) {
                  activeIndex = i;
                  return false; // break
                }
              }
            });

            if (activeIndex >= 0) {
              try {
                $main.slick('slickGoTo', activeIndex);
              } catch (err) {
                console.warn('slick goTo error', err);
              }
            } else {
              console.warn('Could not find activeIndex among original slides', {
                composedMediaId,
                originalIds: $originalSlides.map(function () {
                  return window.jQuery(this).find('[data-media-id]').attr('data-media-id');
                }).get(),
              });
            }
          }
        }

        // Mark the correct thumbnail active using the SAME index
        const thumbsContainerLive =
          document.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
          this.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
          document.querySelector('[id^="Slider-Thumbnails"]') ||
          this.querySelector('[id^="Slider-Thumbnails"]');

        if (thumbsContainerLive) {
          const thumbItemsLive = Array.from(thumbsContainerLive.querySelectorAll('.thumbnail-list__item'));
          // Prefer index-based mapping (index among non-cloned slides)
          if (activeIndex >= 0 && thumbItemsLive[activeIndex]) {
            thumbItemsLive.forEach((t) => t.classList.remove('is-active'));
            thumbItemsLive[activeIndex].classList.add('is-active');
            thumbItemsLive[activeIndex].scrollIntoView?.({ inline: 'center', behavior: 'smooth', block: 'nearest' });
          } else {
            // fallback: match by normalized data-target
            const normalizedTarget = normalize(composedMediaId);
            let matched = thumbItemsLive.find((t) => String(t.dataset.target || '').replace(new RegExp(`^${this.dataset.section || sectionId}-`), '') === normalizedTarget);
            if (!matched) matched = thumbItemsLive.find((t) => t.dataset.target === composedMediaId);
            if (matched) {
              thumbItemsLive.forEach((t) => t.classList.remove('is-active'));
              matched.classList.add('is-active');
              matched.scrollIntoView?.({ inline: 'center', behavior: 'smooth', block: 'nearest' });
            }
          }
        }
      } catch (e) {
        console.warn('final sync failed', e);
      }
    }, 80); // inner wait — tune higher if environment is slower
  }, 40); // initial wait for reinit
}



      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('deferred-media').forEach((wrapper) => {
    const template = wrapper.querySelector('template');
    if (template) {
      wrapper.innerHTML = template.innerHTML; // inject video from template
      const video = wrapper.querySelector('video');
      if (video) {
        video.muted = true;
        video.play().catch((err) => console.log('Autoplay blocked:', err));
      }
    }
  });
});
